import React, { useEffect, useRef, useState } from "react";

import "shaka-player/dist/controls.css";
import { ensure } from "../../utils";

// eslint-disable-next-line @typescript-eslint/no-var-requires
const shaka = require("shaka-player/dist/shaka-player.ui.js");

export interface ControllableShakaPlayerProps {
    manifestUrl: string;
    licenceServer: string;
    licenceCustomData: string;
    accessToken?: string;
    rewindTimestamp: number;
    setCurrentTimestamp(timestamp: number): void;
    setVideoDuration(duration: number): void;
    setVideoHeight(height: number): void;
    onError?(): void;
}

export const ControllableShakaPlayer: React.FunctionComponent<ControllableShakaPlayerProps> = (
    props: ControllableShakaPlayerProps
) => {
    if (!props.licenceServer) {
        throw new Error("Shaka player requires DRM Widevine licence server to be provided");
    }
    const videoComponentRef = useRef<HTMLVideoElement>(null);
    const videoContainerRef = useRef<HTMLDivElement>(null);
    const [isError, setIsError] = useState(false);

    const handleError = () => {
        setIsError(true);

        if (props.onError) {
            props.onError();
        }
    };

    useEffect(() => {
        const video = videoComponentRef.current;
        const videoContainer = videoContainerRef.current;

        ensure(video);

        window.addEventListener("resize", () => setHeight);

        const player = new shaka.Player(video);

        const licenceServer = `${props.licenceServer}&CustomData=${props.licenceCustomData}`;

        player.configure({
            drm: {
                servers: { "com.widevine.alpha": licenceServer }
            }
        });

        const uiConfig = {
            addSeekBar: false
        };

        const ui = new shaka.ui.Overlay(player, videoContainer, video);
        ui.configure(uiConfig);
        ui.getControls();

        player.addEventListener("error", handleError);

        video.addEventListener("timeupdate", () => {
            props.setCurrentTimestamp(video.currentTime);
        });

        registerAuthFilter(player, props.manifestUrl, props.accessToken);

        player.load(props.manifestUrl).then(() => {
            const textTracks = player.getTextTracks();

            props.setVideoDuration(video.duration);
            setHeight(true);

            if (textTracks?.length) {
                player.selectTextTrack(textTracks[0].id);
                player.setTextTrackVisibility(true);
            }
        });
    }, []);

    useEffect(() => {
        if (videoComponentRef.current) {
            videoComponentRef.current.currentTime = props.rewindTimestamp;

            props.setCurrentTimestamp(props.rewindTimestamp);
        }
    }, [props.rewindTimestamp]);

    const setHeight = (isInitial?: boolean) => {
        if (videoComponentRef.current) {
            const offsetHeight = videoComponentRef.current?.offsetHeight;

            props.setVideoHeight(isInitial ? offsetHeight + offsetHeight * 0.13 : offsetHeight);
        }
    };

    useEffect(() => {
        setHeight();
    }, [videoComponentRef.current?.offsetHeight]);

    const containerStyle = {
        width: "100%",
        height: "auto"
    };

    const videoStyle = {
        width: "100%",
        height: "100%",
        margin: "auto"
    };

    if (isError) {
        return <></>;
    }

    return (
        <div className="video-container" style={containerStyle} ref={videoContainerRef}>
            <video className="shaka-video" ref={videoComponentRef} style={videoStyle} />
        </div>
    );
};

const registerAuthFilter = (
    player: { getNetworkingEngine(): { registerRequestFilter(f: unknown): void } },
    manifestUrl: string,
    accessToken: string | undefined
): void => {
    if (!accessToken) return;
    const cloudFrontHost = new URL(manifestUrl).host;
    player
        .getNetworkingEngine()
        .registerRequestFilter((_type: unknown, request: { headers: Record<string, string>; uris: string[] }) => {
            if (request.uris[0]?.includes(cloudFrontHost)) {
                request.headers["Authorization"] = accessToken;
            }
        });
};
